import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { clearCart, selectCartTotalAmount } from '../../Features/Cart/cartSlice';
import { getCustomerToken, customerApi, getImageUrl, API_BASE } from '../../api/client';
import { getCurrentPosition, reverseGeocodeLatLng, saveDeliveryLocationSnapshot } from '../../utils/locationHelper';
import './Payment.css';
import Seo from '../../Components/Seo/Seo';
import { useSiteSeo } from '../../context/SiteSeoContext';
import { usePrerenderReady } from '../../utils/prerenderReady';
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID || '';

const CHECKOUT_STORAGE_KEY = 'checkout_data';

export default function Payment() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.items);
  const totalPrice = useSelector(selectCartTotalAmount);

  const [checkoutData, setCheckoutData] = useState(null);

  const [placing, setPlacing] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [cartTotals, setCartTotals] = useState(null);
  const [cartTotalsLoading, setCartTotalsLoading] = useState(false);

  const [savedCards, setSavedCards] = useState([]);
  const [savedCardsLoading, setSavedCardsLoading] = useState(false);
  /** 'saved' = charge a stored token; 'new' = full checkout (optional save) */
  const [paymentCardMode, setPaymentCardMode] = useState("new");
  const [selectedTokenId, setSelectedTokenId] = useState("");
  const [saveNewCard, setSaveNewCard] = useState(false);
  const [locationLoadingPay, setLocationLoadingPay] = useState(false);
  const [locationMsgPay, setLocationMsgPay] = useState('');
  const [agreedPolicies, setAgreedPolicies] = useState(false);

  const siteCtx = useSiteSeo();
  const titleSuffix =
    siteCtx.organization_name && String(siteCtx.organization_name).trim()
      ? ` | ${String(siteCtx.organization_name).trim()}`
      : ' | Gem Fashion Studios';

  const loggedIn = Boolean(getCustomerToken());

  useEffect(() => {
    if (!loggedIn) return;
    setSavedCardsLoading(true);
    customerApi("/api/customer/payment-methods")
      .then((d) => {
        const cards = Array.isArray(d.cards) ? d.cards : [];
        setSavedCards(cards);
        if (cards.length > 0) {
          setPaymentCardMode("saved");
          setSelectedTokenId(cards[0].id);
        }
      })
      .catch(() => setSavedCards([]))
      .finally(() => setSavedCardsLoading(false));
  }, [loggedIn]);

  useEffect(() => {
    const stored = sessionStorage.getItem(CHECKOUT_STORAGE_KEY);
    if (stored) {
      try {
        setCheckoutData(JSON.parse(stored));
      } catch (_) {
        navigate('/cart');
      }
    } else {
      navigate('/cart');
    }
  }, [navigate]);

  useEffect(() => {
    if (!checkoutData || cartItems.length === 0) return;
    const subtotalPaise = Math.round((totalPrice || 0) * 100);
    if (subtotalPaise <= 0) return;
    setCartTotalsLoading(true);
    fetch(`${API_BASE}/api/cart/calculate-totals`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        subtotal_paise: subtotalPaise,
        pincode: checkoutData.address?.pincode?.trim() || undefined,
        coupon_code: checkoutData.couponCode?.trim() || undefined,
        items: cartItems.map((i) => ({
          productId: i.productId ?? i.productID,
          quantity: i.quantity || 1,
        })),
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.error) throw new Error(data.error);
        setCartTotals(data);
      })
      .catch(() => setCartTotals(null))
      .finally(() => setCartTotalsLoading(false));
  }, [checkoutData, cartItems, totalPrice]);

  const customerToken = getCustomerToken();
  usePrerenderReady(Boolean(customerToken && checkoutData && cartItems.length > 0));

  if (!customerToken) {
    const redirect = `${location.pathname}${location.search || ''}` || '/payment';
    return <Navigate to={`/loginSignUp?redirect=${encodeURIComponent(redirect)}`} replace />;
  }

  const handlePaymentUseLocation = async () => {
    setOrderError('');
    setLocationMsgPay('');
    setLocationLoadingPay(true);
    try {
      const { latitude, longitude } = await getCurrentPosition();
      const geo = await reverseGeocodeLatLng(latitude, longitude);
      saveDeliveryLocationSnapshot({
        lat: latitude,
        lng: longitude,
        pincode: geo.pincode,
        city: geo.city,
        state: geo.state,
        line1: geo.line1,
      });
      setCheckoutData((prev) => {
        if (!prev) return prev;
        const next = {
          ...prev,
          address: {
            ...(prev.address || {}),
            line1: prev.address?.line1?.trim() ? prev.address.line1 : geo.line1,
            pincode: geo.pincode || prev.address?.pincode || '',
            city: geo.city || prev.address?.city || '',
            state: geo.state || prev.address?.state || '',
          },
        };
        sessionStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(next));
        return next;
      });
      setLocationMsgPay('Delivery area updated from your location.');
    } catch (e) {
      setOrderError(e.message || 'Could not get location.');
    } finally {
      setLocationLoadingPay(false);
    }
  };

  const placeOrder = async () => {
    if (!checkoutData || cartItems.length === 0) return;
    setOrderError('');
    const { address, contact, couponCode } = checkoutData;
    const eventDateForOrder = cartItems.find((i) => i.event_date)?.event_date || '';
    if (!eventDateForOrder) {
      setOrderError('Event date is required. Please go back to cart.');
      return;
    }
    if (!contact?.name?.trim() || !contact?.email?.trim() || !contact?.phone?.trim()) {
      setOrderError('Please fill in name, email and phone.');
      return;
    }
    if (!address?.line1?.trim() || !address?.city?.trim() || !address?.pincode?.trim()) {
      setOrderError('Please fill in address (street, city, pincode).');
      return;
    }
    if (loggedIn && paymentCardMode === "saved" && savedCards.length > 0 && !selectedTokenId) {
      setOrderError("Select a saved card or choose Pay with a new card.");
      return;
    }
    if (!agreedPolicies) {
      setOrderError(
        'Please confirm you agree to our Terms & Conditions and Refund Policy (and Privacy Policy where applicable).'
      );
      return;
    }

    setPlacing(true);
    try {
      const items = cartItems.map((i) => ({
        productId: i.productId ?? i.productID,
        rentDays: i.rent_days ?? 1,
        quantity: i.quantity || 1,
        selected_attributes: i.selectedAttributes && typeof i.selectedAttributes === 'object' ? i.selectedAttributes : undefined,
      }));
      const payload = {
        items,
        event_date: eventDateForOrder,
        contact: { name: contact.name.trim(), email: contact.email.trim(), phone: contact.phone.trim() },
        address: {
          line1: address.line1.trim(),
          line2: (address.line2 || '').trim(),
          city: address.city.trim(),
          state: (address.state || '').trim(),
          pincode: address.pincode.trim(),
        },
        coupon_code: couponCode?.trim() || undefined,
        payment_method: 'razorpay',
      };
      const data = await customerApi('/api/orders', { method: 'POST', body: JSON.stringify(payload) });
      const orderId = data.order_id;
      const totalPaise = data.total_paise || data.amount_paise;

      if (!data.razorpay_order_id || !RAZORPAY_KEY_ID) {
        setOrderError('Razorpay is not configured. Check REACT_APP_RAZORPAY_KEY_ID and server keys, then try again.');
        setPlacing(false);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);
      script.onload = () => {
        const options = {
          key: RAZORPAY_KEY_ID,
          amount: totalPaise,
          currency: data.currency || 'INR',
          order_id: data.razorpay_order_id,
          name: 'Gem Fashion Studio',
          description: 'Rental order',
          prefill: {
            name: contact.name.trim(),
            email: contact.email.trim(),
            contact: contact.phone.trim(),
          },
          modal: {
            ondismiss: () => setPlacing(false),
          },
          handler: async (resp) => {
            try {
              const verifyToken = getCustomerToken();
              const verifyRes = await fetch(`${API_BASE}/api/orders/${orderId}/verify-payment`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(verifyToken ? { Authorization: `Bearer ${verifyToken}` } : {}),
                },
                body: JSON.stringify({
                  razorpay_payment_id: resp.razorpay_payment_id,
                  razorpay_signature: resp.razorpay_signature,
                }),
              });
              const verifyData = await verifyRes.json();
              if (verifyRes.ok && verifyData.ok) {
                sessionStorage.removeItem(CHECKOUT_STORAGE_KEY);
                dispatch(clearCart());
                navigate('/payment/success', { state: { orderId, total: totalPaise / 100 } });
              } else throw new Error(verifyData.error || 'Verification failed');
            } catch (e) {
              setOrderError(e.message || 'Payment verification failed.');
            } finally {
              setPlacing(false);
            }
          },
        };
        const rcid = data.razorpay_customer_id;
        if (rcid) {
          options.customer_id = rcid;
          if (paymentCardMode === "saved" && selectedTokenId) {
            options.token = selectedTokenId;
          }
          if (paymentCardMode === "new" && saveNewCard) {
            options.save = 1;
          }
        }
        const rzp = window.Razorpay && new window.Razorpay(options);
        if (rzp) rzp.open();
        else {
          setOrderError('Razorpay failed to load.');
          setPlacing(false);
        }
      };
    } catch (e) {
      setOrderError(e.message || 'Could not place order.');
      setPlacing(false);
    }
  };

  if (!checkoutData || cartItems.length === 0) {
    return (
      <>
        <Seo
          title={`Payment${titleSuffix}`}
          description="Complete your Razorpay payment for Gem Fashion Studios rental checkout."
          canonicalPath="/payment"
          noindex
        />
        <div className="payment-page">
          <p>Loading…</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Seo
        title={`Secure checkout${titleSuffix}`}
        description="Pay securely with Razorpay for your designer clothing rental."
        canonicalPath="/payment"
        noindex
      />
    <div className="payment-page">
      <div className="payment-container">
        <h1>Payment</h1>
        <Link to="/cart" className="payment-back">← Back to checkout</Link>

        {checkoutData && !checkoutData.address?.pincode?.trim() && (
          <div className="payment-location-prompt">
            <p className="payment-location-prompt-title">Delivery pincode missing</p>
            <p className="payment-location-prompt-text">
              Allow location access so we can set your area for shipping, or go back and enter pincode manually.
            </p>
            <button
              type="button"
              className="payment-location-btn"
              onClick={handlePaymentUseLocation}
              disabled={locationLoadingPay}
            >
              {locationLoadingPay ? 'Getting location…' : 'Use my current location'}
            </button>
          </div>
        )}
        {locationMsgPay && <p className="payment-location-success">{locationMsgPay}</p>}

        {orderError && <p className="payment-error">{orderError}</p>}

        <div className="payment-layout">
          <div className="payment-methods">
            <h3>Payment</h3>
            <div className="payment-option payment-option--static selected">
              <div className="payment-option-content">
                <span className="payment-option-title">Razorpay</span>
                <p>Pay securely with UPI, cards, net banking, or wallets.</p>
              </div>
            </div>

            {loggedIn && (
              <div className="payment-saved-cards">
                {savedCardsLoading ? (
                  <p className="payment-saved-cards-hint">Loading saved cards…</p>
                ) : savedCards.length > 0 ? (
                  <>
                    <p className="payment-saved-cards-title">Card</p>
                    <label className="payment-card-mode">
                      <input
                        type="radio"
                        name="cardMode"
                        checked={paymentCardMode === 'saved'}
                        onChange={() => setPaymentCardMode('saved')}
                      />
                      <span>Use a saved card</span>
                    </label>
                    {paymentCardMode === 'saved' && (
                      <select
                        className="payment-token-select"
                        value={selectedTokenId}
                        onChange={(e) => setSelectedTokenId(e.target.value)}
                        aria-label="Saved card"
                      >
                        {savedCards.map((c) => (
                          <option key={c.id} value={c.id}>
                            {(c.network || 'Card').toUpperCase()} ···· {c.last4 || '****'}
                          </option>
                        ))}
                      </select>
                    )}
                    <label className="payment-card-mode">
                      <input
                        type="radio"
                        name="cardMode"
                        checked={paymentCardMode === 'new'}
                        onChange={() => setPaymentCardMode('new')}
                      />
                      <span>Pay with a new card, UPI, or net banking</span>
                    </label>
                  </>
                ) : null}
                {paymentCardMode === 'new' && (
                  <label className="payment-save-card">
                    <input
                      type="checkbox"
                      checked={saveNewCard}
                      onChange={(e) => setSaveNewCard(e.target.checked)}
                    />
                    <span>Save card for next time (securely via Razorpay)</span>
                  </label>
                )}
              </div>
            )}

            <label className="payment-save-card payment-consent-checkbox">
              <input
                type="checkbox"
                checked={agreedPolicies}
                onChange={(e) => setAgreedPolicies(e.target.checked)}
              />
              <span>
                I agree to the{' '}
                <Link to="/terms">Terms &amp; Conditions</Link>
                {' '}
                and{' '}
                <Link to="/privacy-policy">Privacy Policy</Link>
                {' '}
                and{' '}
                <Link to="/refund-policy">Refund Policy</Link>
              </span>
            </label>

            <button
              className="payment-place-order"
              onClick={placeOrder}
              disabled={placing || cartItems.length === 0 || !agreedPolicies}
            >
              {placing ? 'Placing…' : 'Pay with Razorpay'}
            </button>
          </div>
          <div className="payment-summary">
            <h3>Order summary</h3>
            <div className="payment-items">
              {cartItems.map((item) => (
                <div key={`${item.productID ?? item.productId}-${item.rent_days ?? 1}`} className="payment-item">
                  <img src={getImageUrl(item.frontImg) || item.frontImg} alt={item.productName ? String(item.productName) : ''} />
                  <div>
                    <p>{item.productName}</p>
                    <span>× {item.quantity} — ₹{item.productPrice * item.quantity}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="payment-totals">
              <div><span>Subtotal</span><span>₹{totalPrice.toFixed(2)}</span></div>
              {cartTotals && (
                <>
                  <div><span>{cartTotals.deposit_label || 'Deposit'}</span><span>₹{((cartTotals.deposit_paise || 0) / 100).toFixed(2)}</span></div>
                  <div><span>{cartTotals.shipping_label || 'Shipping'}</span><span>₹{((cartTotals.shipping_paise || 0) / 100).toFixed(2)}</span></div>
                  <div><span>{cartTotals.gst_label || 'GST'}</span><span>₹{((cartTotals.gst_paise || 0) / 100).toFixed(2)}</span></div>
                </>
              )}
              <div className="payment-total-row">
                <span>Total</span>
                <span>₹{cartTotals ? ((cartTotals.total_paise || 0) / 100).toFixed(2) : cartTotalsLoading ? '—' : totalPrice.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
